import React, { useMemo, useState } from 'react';

import './index.less';

import { IngestionScheduleModel } from '../../../../../../gamma-api';
import IbModal from '../../../../../components/common/IbModal';
import IbButton from '../../../../../components/common/IbButton';
import IbTypography from '../../../../../components/common/IbTypography';
import IbSwitch from '../../../../../components/common/IbSwitch';
import Switcher from '../../../../../components/common/Switcher';
import IbTimePicker from '../../../../../components/common/IbTimePicker';
import IbCheckbox from '../../../../../components/common/IbCheckbox';
import { getTranslateFunc, localTimeToUtc, utcTimeToLocal } from '../../../../../utils/stringUtil';

import {
  CONTENT_CLASS_NAME,
  CUSTOM_CLASS_NAME,
  DAILY_CLASS_NAME,
  DAYS_OF_WEEK_COUNT,
  DEFAULT_TIME,
  ENABLE_CLASS_NAME,
  MAIN_CLASS_NAME,
  MODE_CLASS_NAME,
  WEEKDAY_CLASS_NAME,
  WEEKDAY_ITEM_CLASS_NAME,
} from './const';

enum ScheduleMode {
  Daily = 'Daily',
  Custom = 'Custom',
}

interface IScheduleModalProps {
  schedule: IngestionScheduleModel;
  visible: boolean;
  onSave?: (schedule: IngestionScheduleModel) => void;
  onClose?: () => void;
}

const ScheduleModal: React.FC<IScheduleModalProps> = ({ schedule, visible, onSave, onClose }) => {
  const translate = getTranslateFunc('Schedule modal:');

  const [enabled, setEnabled] = useState(schedule.settings.enabled);
  const [dailyTime, setDailyTime] = useState(schedule.settings.daily_time);
  const [weeklyTimes, setWeeklyTimes] = useState(schedule.settings.weekly_times);
  const [scheduleMode, setScheduleMode] = useState(
    schedule.settings.weekly_times && Object.keys(schedule.settings.weekly_times).length
      ? ScheduleMode.Custom
      : ScheduleMode.Daily
  );
  const isSaveDisabled = useMemo(
    () =>
      enabled &&
      ((scheduleMode === ScheduleMode.Daily && !dailyTime) ||
        (scheduleMode === ScheduleMode.Custom && (!weeklyTimes || !Object.keys(weeklyTimes).length))),
    [enabled, scheduleMode, dailyTime, weeklyTimes]
  );

  const onSaveButtonClick = () => {
    onSave?.({
      ...schedule,
      settings: {
        enabled,
        daily_time: scheduleMode === ScheduleMode.Daily ? dailyTime : undefined,
        weekly_times: scheduleMode === ScheduleMode.Custom ? weeklyTimes : undefined,
      },
    });
  };

  const onScheduleModeChange = (value: string | boolean) => {
    setScheduleMode(value as ScheduleMode);
  };

  const dailySchedule = useMemo(() => {
    const time = dailyTime ? utcTimeToLocal(dailyTime) : undefined;

    const onTimeChange = (time: string) => {
      setDailyTime(localTimeToUtc(time));
    };

    return (
      <div className={DAILY_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">{translate('Daily time label')}</IbTypography.Paragraph>
        <IbTimePicker format="HH:mm" value={time} onChange={onTimeChange} />
      </div>
    );
  }, [dailyTime]);

  const customSchedule = useMemo(() => {
    const onDayChange = (key: string) => (checked: boolean) => {
      setWeeklyTimes((prev) => {
        if (checked) {
          return { ...prev, [key]: localTimeToUtc(DEFAULT_TIME) };
        }
        const next = { ...prev };
        delete next[key];
        return next;
      });
    };

    const onDayTimeChange = (key: string) => (time: string) => {
      setWeeklyTimes((prev) => ({ ...prev, [key]: localTimeToUtc(time) }));
    };

    return (
      <div className={CUSTOM_CLASS_NAME}>
        {Array.from({ length: DAYS_OF_WEEK_COUNT }).map((_, index) => {
          const key = String(index);
          const value = weeklyTimes?.[key];
          const time = value ? utcTimeToLocal(value) : undefined;
          return (
            <div key={key} className={WEEKDAY_CLASS_NAME}>
              <div className={WEEKDAY_ITEM_CLASS_NAME}>
                <IbCheckbox value={!!value} onChange={onDayChange(key)} />
                <IbTypography.Paragraph type="secondary">{translate(`Day ${key}`)}</IbTypography.Paragraph>
              </div>
              <IbTimePicker disabled={!value} format="HH:mm" value={time} onChange={onDayTimeChange(key)} />
            </div>
          );
        })}
      </div>
    );
  }, [weeklyTimes]);

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={
        <>
          <IbButton disabled={isSaveDisabled} type="primary" onClick={onSaveButtonClick}>
            {translate('Save')}
          </IbButton>
          <IbButton type="secondary" onClick={onClose}>
            {translate('Cancel')}
          </IbButton>
        </>
      }
      title={translate('Title')}
      visible={visible}
      onCancel={onClose}
    >
      <div className={CONTENT_CLASS_NAME}>
        <IbTypography className={ENABLE_CLASS_NAME}>
          <IbSwitch checked={enabled} title={translate('Enable schedule')} onChange={setEnabled} />
          <IbTypography.Paragraph type="secondary">{translate('Enable schedule')}</IbTypography.Paragraph>
        </IbTypography>
        <IbTypography.Paragraph disabled type="secondary">
          {translate('Description')}
        </IbTypography.Paragraph>
        {enabled && (
          <>
            <Switcher
              className={MODE_CLASS_NAME}
              options={[
                { value: ScheduleMode.Daily, label: translate('Mode: Daily') },
                { value: ScheduleMode.Custom, label: translate('Mode: Custom') },
              ]}
              value={scheduleMode}
              onChange={onScheduleModeChange}
            />
            {scheduleMode === ScheduleMode.Daily && dailySchedule}
            {scheduleMode === ScheduleMode.Custom && customSchedule}
          </>
        )}
      </div>
    </IbModal>
  );
};

export default ScheduleModal;
